import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

import AuthLayout from '../layouts/AuthLayout.vue'
import AppLayout from '../layouts/AppLayout.vue'

const routes: Array<RouteRecordRaw> = [
  // Auth routes (public)
  {
    path: '/auth',
    component: AuthLayout,
    meta: { guest: true },
    children: [
      {
        name: 'login',
        path: 'login',
        component: () => import('../pages/auth/Login.vue'),
      },
      {
        name: 'signup',
        path: 'signup',
        component: () => import('../pages/auth/Signup.vue'),
      },
      { path: '', redirect: { name: 'login' } },
    ],
  },
  // Main app routes (authenticated)
  {
    path: '/',
    component: AppLayout,
    meta: { requiresAuth: true },
    redirect: { name: 'dashboard' },
    children: [
      // Student pages
      {
        name: 'dashboard',
        path: 'dashboard',
        component: () => import('../pages/student/Dashboard.vue'),
      },
      {
        name: 'exercises',
        path: 'exercises',
        component: () => import('../pages/student/ExerciseList.vue'),
      },
      {
        name: 'practice',
        path: 'exercises/:id/practice',
        component: () => import('../pages/student/Practice.vue'),
      },
      {
        name: 'exercise-unit',
        path: 'exercises/unit/:groupId',
        component: () => import('../pages/student/ExerciseUnit.vue'),
      },
      {
        name: 'report',
        path: 'submissions/:id/report',
        component: () => import('../pages/student/ReportDetail.vue'),
      },
      {
        name: 'tutor',
        path: 'tutor',
        component: () => import('../pages/student/Tutor.vue'),
      },
      {
        name: 'history',
        path: 'history',
        component: () => import('../pages/student/History.vue'),
      },
      {
        name: 'statistics',
        path: 'statistics',
        component: () => import('../pages/student/Statistics.vue'),
      },
      // Teacher pages
      {
        name: 'teacher-dashboard',
        path: 'teacher',
        meta: { roles: ['teacher', 'admin'] },
        component: () => import('../pages/teacher/Dashboard.vue'),
      },
      {
        name: 'teacher-exercises',
        path: 'teacher/exercises',
        meta: { roles: ['teacher', 'admin'] },
        component: () => import('../pages/teacher/ExerciseManage.vue'),
      },
      {
        name: 'teacher-exercise-new',
        path: 'teacher/exercises/new',
        meta: { roles: ['teacher', 'admin'] },
        component: () => import('../pages/teacher/ExerciseForm.vue'),
      },
      {
        name: 'teacher-exercise-edit',
        path: 'teacher/exercises/:id/edit',
        meta: { roles: ['teacher', 'admin'] },
        component: () => import('../pages/teacher/ExerciseForm.vue'),
      },
      {
        name: 'teacher-reviews',
        path: 'teacher/reviews',
        meta: { roles: ['teacher', 'admin'] },
        component: () => import('../pages/teacher/Reviews.vue'),
      },
      {
        name: 'teacher-assignments',
        path: 'teacher/assignments',
        meta: { roles: ['teacher', 'admin'] },
        component: () => import('../pages/teacher/AssignmentManage.vue'),
      },
      {
        name: 'teacher-class-profile',
        path: 'teacher/class-profile',
        meta: { roles: ['teacher', 'admin'] },
        component: () => import('../pages/teacher/ClassProfile.vue'),
      },
      // Admin pages
      {
        name: 'admin-users',
        path: 'admin/users',
        meta: { roles: ['admin'] },
        component: () => import('../pages/admin/UserManage.vue'),
      },
      {
        name: 'settings',
        path: 'settings',
        component: () => import('../pages/settings/Settings.vue'),
      },
    ],
  },
  {
    name: '404',
    path: '/404',
    component: () => import('../pages/404.vue'),
  },
  {
    path: '/:pathMatch(.*)*',
    component: () => import('../pages/404.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition
    if (to.hash) return { el: to.hash, behavior: 'smooth' }
    window.scrollTo(0, 0)
  },
  routes,
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()

  // Fetch user if token exists but user not loaded
  if (auth.token && !auth.user) {
    try {
      await auth.fetchUser()
    } catch {
      auth.logout()
    }
  }

  // Auth required
  if (to.matched.some((r) => r.meta.requiresAuth) && !auth.isLoggedIn) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }

  // Guest only (login/signup)
  if (to.matched.some((r) => r.meta.guest) && auth.isLoggedIn) {
    return { name: 'dashboard' }
  }

  // Role-based access
  const requiredRoles = to.meta.roles as string[] | undefined
  if (requiredRoles && auth.user && !requiredRoles.includes(auth.user.role)) {
    return { name: 'dashboard' }
  }
})

export default router
